import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { SecondaryButton, GlassButton } from '@/components/ui/button-variants';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Heart, Camera, Eye, Edit } from 'lucide-react';

interface PetCardProps {
  pet: {
    id: string;
    name: string;
    species: string;
    breed: string | null;
    age: number | null;
    gender: string | null;
    description: string | null;
    avatar_url: string | null;
    posts_count: number;
    created_at: string;
    is_public: boolean;
  };
  onEdit?: (pet: any) => void;
  showActions?: boolean;
}

const PetCard = ({ pet, onEdit, showActions = true }: PetCardProps) => {
  const getSpeciesEmoji = (species: string) => {
    switch (species.toLowerCase()) {
      case 'cane':
      case 'dog':
        return '🐕';
      case 'gatto':
      case 'cat':
        return '🐱';
      case 'coniglio':
      case 'rabbit':
        return '🐰';
      case 'uccello':
      case 'bird':
        return '🐦';
      case 'pesce':
      case 'fish':
        return '🐠';
      case 'tartaruga':
      case 'turtle':
        return '🐢';
      case 'criceto':
      case 'hamster':
        return '🐹';
      default:
        return '🐾';
    }
  };

  return (
    <Link to={`/app/pet/${pet.id}`} className="block">
      <Card className="glass border-white/20 transition-all duration-300 hover:scale-105 hover:glow-primary cursor-pointer">
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            {/* Pet Avatar */}
            <div className="relative">
              <Avatar className="w-16 h-16 ring-2 ring-primary/30">
                <AvatarImage src={pet.avatar_url || undefined} />
                <AvatarFallback className="bg-gradient-to-br from-primary to-secondary text-white text-xl">
                  {getSpeciesEmoji(pet.species)}
                </AvatarFallback>
              </Avatar>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-primary flex items-center justify-center text-xs font-bold text-white">
                {getSpeciesEmoji(pet.species)}
              </div>
            </div>

            {/* Pet Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-white truncate">{pet.name}</h3>
                <Badge variant="secondary" className="text-xs">
                  {pet.species}
                </Badge>
              </div>
              
              <div className="space-y-1">
                {pet.breed && (
                  <p className="text-sm text-muted-foreground truncate">
                    {pet.breed}
                  </p>
                )}
                
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  {pet.age && (
                    <span>{pet.age} {pet.age === 1 ? 'anno' : 'anni'}</span>
                  )}
                  <div className="flex items-center gap-1">
                    <Camera className="w-3 h-3" />
                    <span>{pet.posts_count} post</span>
                  </div>
                </div>
              </div>

              {/* Actions - Only for owner's pets */}
              {showActions && onEdit && (
                <div className="flex gap-2 mt-3 flex-wrap" onClick={(e) => e.preventDefault()}>
                  <GlassButton 
                    size="default" 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onEdit(pet);
                    }} 
                    className="text-xs px-3 py-2 h-8 min-w-[80px] max-w-[100px]"
                  >
                    <Edit className="w-3 h-3 mr-1 flex-shrink-0" />
                    <span className="truncate">Modifica</span>
                  </GlassButton>
                </div>
              )}
            </div>

            {/* Love indicator */}
            <div className="flex flex-col items-center">
              <Heart 
                className="w-6 h-6 text-primary animate-pulse" 
                fill="currentColor" 
              />
              <span className="text-xs text-muted-foreground mt-1">
                {pet.posts_count > 0 ? 'Attivo' : 'Nuovo'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default PetCard;